<script setup lang="ts">
  defineOptions({ name: 'CPInput', inheritAttrs: false })

  // Vue core imports
  import type { PropType } from 'vue'

  // External library imports
  import { HLInput } from '@platform-ui/highrise'

  import type { InputType } from '@platform-ui/highrise'

  // Props definitions
  const props = defineProps({
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    id: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: false,
      default: '',
    },
    modelValue: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    type: {
      type: String as PropType<InputType>,
      required: false,
      default: 'text',
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    maxlength: {
      type: Number,
      required: false,
      default: undefined,
    },
    showCount: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String as PropType<'3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg'>,
      required: false,
      default: 'md',
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    'update:modelValue': [value: string]
  }>()

  // User-defined functions
  const handleInput = (value: string | [string, string] | number | null) => {
    emit('update:modelValue', Array.isArray(value) ? value.join('') : String(value ?? ''))
  }
</script>

<template>
  <HLInput
    v-bind="$attrs"
    :id="props.id"
    :size="props.size"
    :disabled="props.disabled"
    :value="props.modelValue ?? props.value"
    :model-value="props.modelValue ?? props.value"
    :placeholder="props.placeholder"
    :type="props.type"
    :readonly="props.readonly"
    :maxlength="props.maxlength"
    :show-count="props.showCount"
    :style="{
      '--n-border-radius': '4px',
    }"
    @update:model-value="handleInput"
  >
    <template v-if="$slots.prefix" #prefix>
      <slot name="prefix" />
    </template>
    <template v-if="$slots.suffix" #suffix>
      <slot name="suffix" />
    </template>
  </HLInput>
</template>

<!--
  HighRise input overrides — unscoped because HighRise sets
  --n-text-color on the wrapper but also injects `color` directly on
  inner element nodes (<input>, <textarea>, count/suffix spans). When
  it does, the CSS-variable chain from a scoped wrapper cannot reach
  those nodes, so the user's font-color token gets ignored. Targeting
  the global .hr-input descendants directly keeps the chain consistent.
  HighRise injects --n-* custom properties as inline styles via its JS
  theme engine, so !important is required to beat inline specificity.
-->
<style>
  /* Background, text, border, caret for the input wrapper */
  .hr-input {
    --n-color: var(--cp-background-500) !important;
    --n-color-focus: var(--cp-background-500) !important;
    /* Disabled: subtle background — 40% of heavy token blended with normal bg ≈ #f5f5f5 */
    --n-color-disabled: color-mix(in srgb, var(--cp-background-600) 40%, var(--cp-background-500)) !important;
    /* Use the user's explicit font-color token so white/dark themes apply correctly */
    --n-text-color: var(--cp-brand-font) !important;
    --n-text-color-disabled: var(--cp-font-400) !important;
    --n-placeholder-color: color-mix(in oklab, var(--cp-brand-font) 40%, transparent) !important;
    --n-border: 1px solid var(--cp-border-500) !important;
    --n-border-hover: 1px solid var(--cp-primary-500) !important;
    --n-border-focus: 1px solid var(--cp-primary-500) !important;
    --n-border-disabled: 1px solid var(--cp-border-500) !important;
    --n-caret-color: var(--cp-primary-500) !important;
  }

  /* FOCUS-RING
   * HighRise's shared input theme hard-codes the focus-ring glow to `#D1E0FF`
   * (see highrise481.mjs → commonInputStyles.boxShadowFocus). HighRise emits
   * that value as an inline --n-box-shadow-focus on every input-like component
   * so the focus ring stays blue regardless of the active brand. Override it
   * with --cp-primary-100 so the glow always matches the primary colour. */
  .hr-input {
    --n-box-shadow-focus: 0px 0px 0px 4px var(--cp-primary-100), var(--shadow-xs) !important;
  }

  /* DIRECT TEXT COLOR
   * HighRise injects `color` directly on the inner <input>/<textarea>
   * elements, bypassing the wrapper's --n-text-color chain. Set `color`
   * on those elements directly so the user's fontColor token is always
   * respected. -webkit-text-fill-color is required on WebKit because it
   * takes precedence over `color` for input rendering. */
  .hr-input input,
  .hr-input textarea {
    color: var(--cp-brand-font) !important;
    -webkit-text-fill-color: var(--cp-brand-font) !important;
  }

  /* Disabled text color — cover both hr-* and n-* class variants HighRise emits */
  .hr-input.hr-input--disabled input,
  .hr-input.n-input--disabled input,
  .hr-input.hr-input--disabled textarea,
  .hr-input.n-input--disabled textarea,
  .hr-input input:disabled,
  .hr-input textarea:disabled {
    color: var(--cp-font-400) !important;
    -webkit-text-fill-color: var(--cp-font-400) !important;
  }

  .hr-input input::placeholder,
  .hr-input textarea::placeholder {
    color: color-mix(in oklab, var(--cp-brand-font) 40%, transparent) !important;
    -webkit-text-fill-color: color-mix(in oklab, var(--cp-brand-font) 40%, transparent) !important;
    opacity: 1 !important; /* Firefox resets opacity on ::placeholder */
  }

  /* Count / suffix / prefix text nodes inside the input wrapper */
  .hr-input .hr-input__count,
  .hr-input .hr-input__suffix,
  .hr-input .hr-input__prefix {
    color: var(--cp-brand-font) !important;
  }

  /* Placeholder overlay rendered by Naive UI — keep muted.
     Double-class specificity beats the legacy grouped rule that included
     .hr-input__placeholder with --cp-brand-font. */
  .hr-input.hr-input .hr-input__placeholder {
    color: var(--n-placeholder-color) !important;
  }

  /* Disabled state — same background as active; muted text is the sole disabled signal */
  .hr-input .hr-input__input-el[disabled] {
    background-color: color-mix(in srgb, var(--cp-background-600) 40%, var(--cp-background-500)) !important;
  }

  .hr-input[disabled] {
    --n-color-disabled: color-mix(in srgb, var(--cp-background-600) 40%, var(--cp-background-500)) !important;
  }
</style>
